/**
 * Currency detection utility based on user's timezone/location
 */

import type { CurrencyCode } from '~/lib/types/directus';

/**
 * Get currency based on user's location (timezone-based detection)
 * Falls back to locale if timezone detection fails
 */
export function getCurrencyFromLocale(): CurrencyCode {
  if (typeof window === 'undefined') return 'USD';
  
  // Try timezone-based detection first (more accurate for location)
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Mapping of timezone regions to currencies
    const timeZoneToCurrency: Record<string, CurrencyCode> = {
      // Americas
      'America/New_York': 'USD',
      'America/Chicago': 'USD',
      'America/Denver': 'USD',
      'America/Los_Angeles': 'USD',
      'America/Phoenix': 'USD',
      'America/Toronto': 'CAD',
      'America/Vancouver': 'CAD',
      'America/Mexico_City': 'MXN',
      'America/Sao_Paulo': 'BRL',
      
      // Europe
      'Europe/London': 'GBP',
      'Europe/Berlin': 'EUR',
      'Europe/Paris': 'EUR',
      'Europe/Rome': 'EUR',
      'Europe/Madrid': 'EUR',
      'Europe/Amsterdam': 'EUR',
      'Europe/Brussels': 'EUR',
      'Europe/Vienna': 'EUR',
      'Europe/Dublin': 'EUR',
      'Europe/Lisbon': 'EUR',
      'Europe/Helsinki': 'EUR',
      'Europe/Athens': 'EUR',
      'Europe/Stockholm': 'SEK',
      'Europe/Oslo': 'NOK',
      'Europe/Copenhagen': 'DKK',
      'Europe/Zurich': 'CHF',
      'Europe/Warsaw': 'PLN',
      'Europe/Prague': 'CZK',
      'Europe/Budapest': 'HUF',
      'Europe/Bucharest': 'RON',
      'Europe/Sofia': 'BGN',
      'Europe/Zagreb': 'HRK',
      'Europe/Moscow': 'RUB',
      'Europe/Istanbul': 'TRY',
      
      // Asia
      'Asia/Jerusalem': 'ILS',
      'Asia/Tel_Aviv': 'ILS',
      'Asia/Dubai': 'AED',
      'Asia/Riyadh': 'SAR',
      'Asia/Doha': 'QAR',
      'Asia/Kuwait': 'KWD',
      'Asia/Bahrain': 'BHD',
      'Asia/Muscat': 'OMR',
      'Asia/Amman': 'JOD',
      'Asia/Beirut': 'LBP',
      'Asia/Tokyo': 'JPY',
      'Asia/Shanghai': 'CNY',
      'Asia/Hong_Kong': 'HKD',
      'Asia/Singapore': 'SGD',
      'Asia/Seoul': 'KRW',
      'Asia/Kolkata': 'INR',
      
      // Pacific
      'Australia/Sydney': 'AUD',
      'Australia/Melbourne': 'AUD',
      
      // Africa
      'Africa/Cairo': 'EGP',
      'Africa/Johannesburg': 'ZAR',
      'Africa/Casablanca': 'MAD',
      'Africa/Tunis': 'TND',
      'Africa/Algiers': 'DZD',
      'Africa/Tripoli': 'LYD',
    };
    
    // Check exact timezone match
    if (timeZoneToCurrency[timeZone]) {
      return timeZoneToCurrency[timeZone];
    }
    
    // Check timezone prefix (e.g., "America/", "Europe/", "Asia/")
    const prefix = timeZone.split('/')[0];
    const region = timeZone.split('/')[1];
    
    // Regional defaults
    if (prefix === 'America') {
      if (region?.includes('Toronto') || region?.includes('Vancouver') || region?.includes('Montreal')) return 'CAD';
      if (region?.includes('Mexico')) return 'MXN';
      if (region?.includes('Sao') || region?.includes('Brazil')) return 'BRL';
      return 'USD';
    }
    if (prefix === 'Europe') {
      if (region?.includes('London')) return 'GBP';
      if (region?.includes('Stockholm')) return 'SEK';
      if (region?.includes('Oslo')) return 'NOK';
      if (region?.includes('Copenhagen')) return 'DKK';
      if (region?.includes('Zurich')) return 'CHF';
      if (region?.includes('Warsaw')) return 'PLN';
      if (region?.includes('Prague')) return 'CZK';
      if (region?.includes('Budapest')) return 'HUF';
      if (region?.includes('Bucharest')) return 'RON';
      if (region?.includes('Sofia')) return 'BGN';
      if (region?.includes('Zagreb')) return 'HRK';
      if (region?.includes('Moscow')) return 'RUB';
      if (region?.includes('Istanbul')) return 'TRY';
      return 'EUR'; // Most of Europe uses EUR
    }
    if (prefix === 'Asia') {
      if (region?.includes('Jerusalem') || region?.includes('Tel_Aviv')) return 'ILS';
      if (region?.includes('Tokyo')) return 'JPY';
      if (region?.includes('Shanghai') || region?.includes('Beijing')) return 'CNY';
      if (region?.includes('Hong_Kong')) return 'HKD';
      if (region?.includes('Singapore')) return 'SGD';
      if (region?.includes('Seoul')) return 'KRW';
      if (region?.includes('Kolkata') || region?.includes('Mumbai') || region?.includes('Delhi')) return 'INR';
      if (region?.includes('Dubai')) return 'AED';
      if (region?.includes('Riyadh')) return 'SAR';
    }
    if (prefix === 'Australia' || prefix === 'Pacific') {
      return 'AUD';
    }
    if (prefix === 'Africa') {
      if (region?.includes('Johannesburg')) return 'ZAR';
      if (region?.includes('Cairo')) return 'EGP';
    }
  } catch (error) {
    console.warn('Timezone detection failed, falling back to locale', error);
  }
  
  // Fallback to locale-based detection
  const locale = navigator.language || navigator.languages?.[0] || 'en-US';
  const country = locale.split('-')[1]?.toUpperCase();
  
  const countryToCurrency: Record<string, CurrencyCode> = {
    'US': 'USD',
    'GB': 'GBP',
    'DE': 'EUR',
    'FR': 'EUR',
    'IT': 'EUR',
    'ES': 'EUR',
    'NL': 'EUR',
    'BE': 'EUR',
    'AT': 'EUR',
    'IE': 'EUR',
    'PT': 'EUR',
    'FI': 'EUR',
    'GR': 'EUR',
    'JP': 'JPY',
    'CA': 'CAD',
    'AU': 'AUD',
    'CH': 'CHF',
    'CN': 'CNY',
    'IN': 'INR',
    'KR': 'KRW',
    'BR': 'BRL',
    'MX': 'MXN',
    'ZA': 'ZAR',
    'SG': 'SGD',
    'HK': 'HKD',
    'NO': 'NOK',
    'SE': 'SEK',
    'DK': 'DKK',
    'PL': 'PLN',
    'CZ': 'CZK',
    'HU': 'HUF',
    'RO': 'RON',
    'BG': 'BGN',
    'HR': 'HRK',
    'RU': 'RUB',
    'TR': 'TRY',
    'IL': 'ILS',
    'AE': 'AED',
    'SA': 'SAR',
  };
  
  return countryToCurrency[country] || 'USD';
}

